<template>
  <page-template :is-loading="isLoading" :data-exists="selectedContentFilterRuleData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('contentFilterRule.configPage.newPageHeadline')
          : $t('contentFilterRule.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Content Filter Rules List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedContentFilterRuleData && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          v-if="selectedContentFilterRule"
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedContentFilterRule"
          @update:value="switchContentFilterRule($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('contentFilterRule.configPage.switchTooltip')"
          classes="switch-content-filter-rule switch-page-entry"
          data-qa="switch-content-filter-rule"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedContentFilterRuleData && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateContentFilterRule()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadContentFilterRule()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteContentFilterRuleModal()"
          class="delete-button"
          :disabled="isLoading"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isContentFilterRuleValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('contentFilterRule.configPage.name')"
              :placeholder="$t('contentFilterRule.configPage.name')"
              v-model:inputValue="selectedContentFilterRuleData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('contentFilterRule.configPage.description')"
              v-model:inputValue="selectedContentFilterRuleData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="category"
              classes="category"
              data-qa="category-input"
              :label="$t('contentFilterRule.category')"
              :placeholder="$t('contentFilterRule.category')"
              v-model:inputValue="selectedContentFilterRuleData.category"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="subcategory"
              classes="subcategory"
              data-qa="subcategory-input"
              :label="$t('contentFilterRule.subcategory')"
              :placeholder="$t('contentFilterRule.subcategory')"
              v-model:inputValue="selectedContentFilterRuleData.subcategory"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="riskLevels.map((riskLevel) => [riskLevel, riskLevel])"
              v-model:value="selectedContentFilterRuleData.risk"
              :label="$t('contentFilterRule.configPage.riskLevel')"
              class="risk-level-selection"
              data-qa="risk-level-selection"
              :disabled="readOnly"
            />
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <tags-selection
              :value="selectedContentFilterRuleData.tags"
              @update:value="selectedContentFilterRuleData.tags = $event"
              :label="$t('contentFilterRule.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="rbz-field">
        <rbz-form-input
          name="logMessage"
          classes="log-message"
          data-qa="log-message-input"
          :label="$t('contentFilterRule.configPage.logMessage')"
          :placeholder="$t('contentFilterRule.configPage.logMessage')"
          v-model:inputValue="selectedContentFilterRuleData.msg"
          :disabled="readOnly"
        />
      </div>
      <div class="rbz-field">
        <rbz-form-input
          name="match"
          classes="match-regex"
          data-qa="match-regex-input"
          :label="$t('contentFilterRule.configPage.match')"
          :placeholder="$t('contentFilterRule.configPage.match')"
          :is-invalid="!isMatchValid"
          v-model:inputValue="selectedContentFilterRuleData.operand"
          :disabled="readOnly"
        />
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadContentFilterRule(selectedContentFilterRule);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteContentFilterRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteContentFilterRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteContentFilterRuleModal = false"
        @closeModal="showDeleteContentFilterRuleModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedContentFilterRuleData.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import { BasicDocument, ContentFilterRule, DocumentName } from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import GitHistory from '@/components/GitHistory.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ContentFilterRulesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzCustomTag,
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    GitHistory,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    TagsSelection,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      selectedContentFilterRule: null as string,
      selectedContentFilterRuleData: null as ContentFilterRule,
      originalContentFilterRuleData: null as ContentFilterRule,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      showDeleteContentFilterRuleModal: false,
      riskLevels: [1, 2, 3, 4, 5],
      isNew: false,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('contentFilterRule.plural'),
        singular: this.$t('contentFilterRule.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/content-filter-rules/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedContentFilterRuleData, this.originalContentFilterRuleData);
    },

    isMatchValid(): boolean {
      const newMatch = this.selectedContentFilterRuleData.operand.trim();
      const isMatchEmpty = newMatch === '';
      return !isMatchEmpty;
    },

    automaticTags(): string[] {
      const allTag = `cf-all`;
      const name = this.selectedContentFilterRuleData.name;
      const ruleTag = `cf-rule-name:${this.tagsStore.textToTag(name)}`;
      const risk = this.selectedContentFilterRuleData.risk;
      const riskTag = `cf-rule-risk:${risk}`;
      const category = this.selectedContentFilterRuleData.category;
      const categoryTag = `cf-rule-category:${this.tagsStore.textToTag(category)}`;
      const subcategory = this.selectedContentFilterRuleData.subcategory;
      const subcategoryTag = `cf-rule-subcategory:${this.tagsStore.textToTag(subcategory)}`;
      return [allTag, ruleTag, riskTag, categoryTag, subcategoryTag];
    },

    isContentFilterRuleValid(): boolean {
      const trimmedName = this.selectedContentFilterRuleData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedContentFilterRuleData.name &&
        (trimmedName === this.originalContentFilterRuleData.name.toLowerCase().trim() ||
          !nameExists);
      return this.isMatchValid && nameValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('content-filter-rules'));
    },

    switchContentFilterRule(contentFilterRuleId: string) {
      this.navigateTo(this.getEditPath('content-filter-rules', contentFilterRuleId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-rules`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    async loadContentFilterRule(contentFilterRuleId: string) {
      this.setPageLoadingStatus(true);
      this.selectedContentFilterRule = contentFilterRuleId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRuleId}`,
      });
      this.selectedContentFilterRuleData = response?.data || {};
      this.originalContentFilterRuleData = _.cloneDeep(this.selectedContentFilterRuleData);
      this.setPageLoadingStatus(false);
    },

    async duplicateContentFilterRule() {
      this.setPageLoadingStatus(true);
      const contentFilterRuleToAdd = _.cloneDeep(this.selectedContentFilterRuleData);
      contentFilterRuleToAdd.id = DatasetsUtils.generateUUID2();
      contentFilterRuleToAdd.name = Utils.generateUniqueEntityName(
        contentFilterRuleToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRuleToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: contentFilterRuleToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchContentFilterRule(contentFilterRuleToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadContentFilterRule() {
      Utils.downloadFile('content-filter-rule', 'json', this.selectedContentFilterRuleData);
    },

    openDeleteContentFilterRuleModal() {
      this.showDeleteContentFilterRuleModal = true;
    },

    async deleteContentFilterRule() {
      this.isDeleteLoading = true;
      const contentFilterRuleId = this.selectedContentFilterRule;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === contentFilterRuleId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedContentFilterRule = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRuleId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }

      this.selectedContentFilterRuleData.name = this.selectedContentFilterRuleData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/content-filter-rules/${this.selectedContentFilterRuleData.id}`,
        data: this.selectedContentFilterRuleData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedContentFilterRuleData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
        }
      }
      this.originalContentFilterRuleData = _.cloneDeep(this.selectedContentFilterRuleData);
      this.setPageLoadingStatus(false);
    },

    newContentFilterRule(): ContentFilterRule {
      const factory = DatasetsUtils.newEntityFactory['content-filter-rules'];
      return factory && factory();
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedContentFilterRuleData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'content-filter-rules') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadContentFilterRule(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Content Filter Rule', 'Content Filter Rules Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            await this.tagsStore.loadTags();
            this.selectedContentFilterRuleData = this.newContentFilterRule();
            this.selectedContentFilterRuleData.name = Utils.generateUniqueEntityName(
              this.selectedContentFilterRuleData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalContentFilterRuleData = _.cloneDeep(this.selectedContentFilterRuleData);
          } else {
            await this.loadDropdownData();
            const contentFilterRuleId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === contentFilterRuleId;
            });
            if (dataExists) {
              await this.loadContentFilterRule(contentFilterRuleId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedContentFilterRuleData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
